class AwardsList {
  constructor(container) {
    this.container = container;
    this.groupsContainer = container.querySelector('[data-groups-container]');
    this.columns = Array.from(container.querySelectorAll('[data-column]'));
    
    if (!this.groupsContainer || this.columns.length === 0) return;
    
    this.init();
    
    // Re-distribute on window resize (debounced)
    this.resizeTimeout = null;
    window.addEventListener('resize', () => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => this.distributeGroups(), 250);
    });
  }
  
  init() {
    // Wait for fonts and images to load before distributing
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => this.distributeGroups());
    } else {
      // Fallback if Font Loading API not available
      setTimeout(() => this.distributeGroups(), 100);
    }
  }
  
  distributeGroups() {
    // Get all groups from the hidden container
    const groups = Array.from(this.groupsContainer.querySelectorAll('.awards-list__group'));
    
    if (groups.length === 0) return;
    
    // Clear existing columns
    this.columns.forEach(column => column.innerHTML = '');
    
    // Check if mobile (single column)
    if (window.innerWidth < 750) {
      // On mobile, put everything in first column
      groups.forEach(group => {
        this.columns[0].appendChild(group.cloneNode(true));
      });
      return;
    }
    
    // Determine number of columns based on screen size
    // Tablet (750px-1023px): 2 columns
    // Desktop (1024px+): 3 columns
    const numColumns = window.innerWidth < 1024 ? 2 : 3;
    
    // Track height of each active column
    const columnHeights = new Array(numColumns).fill(0);
    
    groups.forEach(group => {
      // Clone the group
      const clonedGroup = group.cloneNode(true);
      
      // Find shortest column
      const shortestColumnIndex = columnHeights.indexOf(Math.min(...columnHeights));
      
      // Add to shortest column
      this.columns[shortestColumnIndex].appendChild(clonedGroup);
      
      // Update column height (approximate based on content)
      // We'll use a rough calculation since we can't measure accurately before insertion
      const itemCount = clonedGroup.querySelectorAll('.awards-list__award, .awards-list__separator, .awards-list__subtitle-block').length;
      const headingCount = clonedGroup.querySelectorAll('.awards-list__heading-block').length;
      
      // Rough height calculation (heading ~60px, each item ~30px, group margin ~40px)
      const estimatedHeight = (headingCount * 60) + (itemCount * 30) + 40;
      columnHeights[shortestColumnIndex] += estimatedHeight;
    });
    
    // After distribution, get actual heights and rebalance if needed
    requestAnimationFrame(() => {
      const actualHeights = this.columns.map(col => col.offsetHeight);
      const maxHeight = Math.max(...actualHeights);
      const minHeight = Math.min(...actualHeights);
      
      // If difference is too large (more than 20%), try one rebalance
      if (maxHeight - minHeight > maxHeight * 0.2) {
        this.rebalanceColumns();
      }
    });
  }
  
  rebalanceColumns() {
    // Get all groups with their actual heights
    const columnGroups = this.columns.map(column => 
      Array.from(column.querySelectorAll('.awards-list__group')).map(group => ({
        element: group,
        height: group.offsetHeight
      }))
    );
    
    // Calculate actual column heights
    const columnHeights = columnGroups.map(groups => 
      groups.reduce((sum, group) => sum + group.height, 0)
    );
    
    // Find tallest and shortest columns
    const tallestIndex = columnHeights.indexOf(Math.max(...columnHeights));
    const shortestIndex = columnHeights.indexOf(Math.min(...columnHeights));
    
    // If there's a significant difference, move last group from tallest to shortest
    if (columnHeights[tallestIndex] - columnHeights[shortestIndex] > 100) {
      const tallestGroups = columnGroups[tallestIndex];
      if (tallestGroups.length > 1) {
        const lastGroup = tallestGroups[tallestGroups.length - 1].element;
        this.columns[shortestIndex].appendChild(lastGroup);
      }
    }
  }
}

// Initialize all awards lists
document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-awards-grid]').forEach(grid => {
    new AwardsList(grid);
  });
});

// Re-initialize on Shopify theme editor updates
if (window.Shopify && window.Shopify.designMode) {
  document.addEventListener('shopify:section:load', (event) => {
    const grid = event.target.querySelector('[data-awards-grid]');
    if (grid) {
      new AwardsList(grid);
    }
  });
}
